import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { containerVariants, itemVariants } from '../lib/animations';
import HomeV2Layout from '../components/home-v2/HomeV2Layout';
import { useChild, useChildAssessments } from '../hooks/useAssessment';
import { useActivityStats } from '../hooks/useActivityStats';
import { useChildSummary } from '../hooks/useChildSummary';
import SparksHistoryCard from '../components/assessment/SparksHistoryCard';
import CategoryCard from '../components/assessment/CategoryCard';
import type { CategoryCardState } from '../components/assessment/CategoryCard';
import ProfileSkeleton from '../components/assessment/ProfileSkeleton';
import { getChildAvatarSrc } from '../utils/childAvatars';
import {
  PROFILE_COLORS,
  PROFILE_TYPOGRAPHY,
  PROFILE_RESPONSIVE,
  CATEGORY_DESCRIPTIONS,
} from '../components/assessment/constants';
import type { ChildAssessmentSummary } from '../types/assessment.types';
import { V1_CATEGORIES } from '../constants/onboardingFlow';
import { useTimeOfDay } from '../components/home-v2';
import { buildProfileGradient } from '../components/home-v2/HomeV2Background';
//import MoodReportSection from '../components/mood/MoodReportSection';
import { CheckInTransitionOverlay, useCheckInTransition } from '../components/home-v2/check-in-transition';
import { useContentManager } from './HomeV2Page';

// Assessment cadence in days (for needs reassessment check)
const ASSESSMENT_CADENCE_DAYS = 14;

export default function ChildProfilePage() {
  const { track } = useAnalytics();
  const navigate = useNavigate();

  // Get childId from localStorage (set by JourneyPage)
  const storedChildId = localStorage.getItem('childId');
  const childId = storedChildId ? parseInt(storedChildId, 10) : 0;

  const { config } = useTimeOfDay();

  useEffect(() => {
    if (childId) {
      track('child_profile_view', { child_id: String(childId) });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  const {
    //cloudRef,
    isTransitioning,
    cloudRect,
    startTransition,
    onTransitionComplete,
    // resetTransition,
  } = useCheckInTransition();

  // Fetch real data
  const { data: child, isLoading: childLoading } = useChild(childId);
  const { data: assessments, isLoading: assessmentsLoading } = useChildAssessments(childId);
  const { data: activityStats, isLoading: statsLoading } = useActivityStats(childId);
  const { summary, isGenerating: summaryGenerating, error: summaryError } = useChildSummary(childId || null);

  const isLoading = childLoading || assessmentsLoading || statsLoading;

  /**
   * Determine the card state for each v1 category.
   * - Find the most recent completed assessment for this category
   * - If no assessment: notAssessed
   * - If assessment exists but older than cadence: needsReassessment
   * - Otherwise: completed with stage
   */
  function getCategoryCardState(
    categoryKey: string,
    allAssessments: ChildAssessmentSummary[] | undefined
  ): CategoryCardState {
    if (!allAssessments || allAssessments.length === 0) {
      return { type: 'notAssessed' };
    }

    // Assessments are ordered most recent first from the API
    const latest = allAssessments.find((a) => a.categoryKey === categoryKey);
    if (!latest || !latest.overallStage) {
      return { type: 'notAssessed' };
    }

    // Check if needs reassessment (cadence exceeded)
    const completedDate = new Date(latest.completedAt);
    const daysSince = Math.floor(
      (Date.now() - completedDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (daysSince > ASSESSMENT_CADENCE_DAYS) {
      return { type: 'needsReassessment', stage: latest.overallStage };
    }

    return { type: 'completed', stage: latest.overallStage };
  }

  /**
   * Get the description text for a category card based on its state.
   */
  function getCategoryDescription(categoryKey: string, state: CategoryCardState): string {
    const descriptions = CATEGORY_DESCRIPTIONS[categoryKey];
    if (!descriptions) return '';

    if (state.type === 'notAssessed') return descriptions.notAssessed;
    return descriptions[state.stage] || '';
  }

  /**
   * Handle category card tap.
   * - If not assessed: navigate to assessment for that category
   * - If assessed/needs reassessment: navigate to category detail
   */
  function handleCategoryTap(categoryKey: string, categoryName: string, state: CategoryCardState) {
    track('category_detail_view', { child_id: String(childId), category_key: categoryKey, category_name: categoryName });
    console.log("state", state)
    if (state.type === 'notAssessed') {
      navigate(`/assessment/${childId}?category=${categoryKey}`);
    } else {
      // Find the categoryId from assessments
      const assessment = assessments?.find((a) => a.categoryKey === categoryKey);
      if (assessment) {
        navigate(`/profile/${childId}/category/${assessment.categoryId}`);
      }
    }
  }

  // Custom gradient for profile screen — solid teal until ~195px, smooth fade to page bg by 534px (matches Figma BG node)
  //const profileGradient = `linear-gradient(180deg, #01A7C8 0%, #01A7C8 140px, #F5F7FA 534px, #F5F7FA 100%)`;

  const {
    hideHeaderAvatar,
    hideSparksHistory,
  //  hideMoodsReport
  } = useContentManager()

  const overrideBackground = hideSparksHistory ? '#F5F7FA' : undefined

  const profileGradient = buildProfileGradient(config, overrideBackground);

  return (
    <>
      <HomeV2Layout
        activeTab="journey"
        contentStyle="transparent"
        backgroundGradient={profileGradient}
        showCloudLogo
        timeOfDayConfig={config}
        onCloudClick={startTransition}
        showAvatar={!hideHeaderAvatar}
      >
        {isLoading ? (
          <ProfileSkeleton />
        ) : (
          <motion.div
            className={`flex flex-col ${PROFILE_RESPONSIVE.sectionGapClasses} pb-8`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* "Your Activity" title on teal bg */}
            {
              !hideSparksHistory ? (
                <motion.div variants={itemVariants} className="pt-2 pb-4">
                  <h1
                    className={`${PROFILE_TYPOGRAPHY.pageTitle.weight} ${PROFILE_TYPOGRAPHY.pageTitle.sizeClasses} ${PROFILE_TYPOGRAPHY.pageTitle.tracking}`}
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      color: PROFILE_COLORS.white,
                    }}
                  >
                    Your Activity
                  </h1>
                </motion.div>
              ) : (<div></div>)
            }

            {/* Sparks History Card */}
            {
              !hideSparksHistory ? (
                <motion.div variants={itemVariants}>
                  <SparksHistoryCard stats={activityStats} />
                </motion.div>
              ) : null
            }

            {/* Moods Report Card (v2 - live data) */}
            {/*!hideMoodsReport ? <MoodReportSection /> : null */}

            {/* Insights Section */}
            <motion.div
              variants={itemVariants}
              className="flex flex-col gap-4"
            >
              {/* Child Info Pill + Add Child Button */}
              <div className="flex items-center gap-3">
                {/* Child Pill */}
                {child && (
                  <div
                    className="flex items-center gap-3 px-4"
                    style={{
                      backgroundColor: PROFILE_COLORS.childPillBg,
                      borderRadius: '48px',
                      height: '75px',
                    }}
                  >
                    {/* Avatar */}
                    <div
                      className="w-[59px] h-[59px] rounded-full flex-shrink-0 overflow-hidden"
                      style={{ border: '3px solid white' }}
                    >
                      <img
                        src={getChildAvatarSrc(child.avatarKey)}
                        alt={child.nickname}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span
                        className={`${PROFILE_TYPOGRAPHY.childName.weight} ${PROFILE_TYPOGRAPHY.childName.sizeClasses}`}
                        style={{
                          fontFamily: 'DM Sans, sans-serif',
                          color: PROFILE_COLORS.childPillText,
                        }}
                      >
                        {child.nickname}
                      </span>
                      <span
                        className={`${PROFILE_TYPOGRAPHY.childAge.weight} ${PROFILE_TYPOGRAPHY.childAge.sizeClasses}`}
                        style={{
                          fontFamily: 'DM Sans, sans-serif',
                          color: PROFILE_COLORS.childPillAge,
                        }}
                      >
                        {child.fromAge} years old
                      </span>
                    </div>
                  </div>
                )}

                {/* Add Child Button (visible but non-functional in v1) */}
                {/* <button
                type="button"
                disabled
                className="flex items-center gap-2 px-3 py-2 opacity-50 cursor-not-allowed"
                style={{
                  border: `2px solid ${PROFILE_COLORS.addChildBorder}`,
                  borderRadius: '24px',
                }}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: PROFILE_COLORS.addChildIconBg }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 3v8M3 7h8" stroke={PROFILE_COLORS.addChildText} strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
                <span
                  className="text-sm font-medium whitespace-nowrap"
                  style={{ color: PROFILE_COLORS.addChildText }}
                >
                  Add child
                </span>
              </button> */}
              </div>

              {/* Summary Section (AI-generated) */}
              {!summaryError && (summary || summaryGenerating) && (
                <div className="flex flex-col gap-2">
                  <h3
                    className={`${PROFILE_TYPOGRAPHY.insightTitle.weight} ${PROFILE_TYPOGRAPHY.insightTitle.sizeClasses} ${PROFILE_TYPOGRAPHY.insightTitle.tracking}`}
                    style={{
                      fontFamily: 'DM Sans, sans-serif',
                      color: PROFILE_COLORS.textPrimary,
                    }}
                  >
                    Where the sparks will focus
                  </h3>

                  {summaryGenerating && (
                    <div className="flex items-center gap-3" style={{ color: PROFILE_COLORS.textSecondary }}>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600" />
                      <p className={`${PROFILE_TYPOGRAPHY.body.weight} ${PROFILE_TYPOGRAPHY.body.sizeClasses}`}
                        style={{ fontFamily: 'DM Sans, sans-serif' }}>
                        Generating personalized summary...
                      </p>
                    </div>
                  )}

                  {summary && !summaryGenerating && (
                    <>
                      {summary.summaryText.split('\n\n').map((paragraph, i) => (
                        <p
                          key={i}
                          className={`${PROFILE_TYPOGRAPHY.body.weight} ${PROFILE_TYPOGRAPHY.body.sizeClasses}`}
                          style={{
                            fontFamily: 'DM Sans, sans-serif',
                            color: PROFILE_COLORS.textSecondary,
                          }}
                        >
                          {paragraph}
                        </p>
                      ))}
                    </>
                  )}
                </div>
              )}
            </motion.div>

            {/* Category Cards */}
            <motion.div variants={itemVariants} className="flex flex-col gap-2">
              {V1_CATEGORIES.map(({ key, name }) => {
                const state = getCategoryCardState(key, assessments);
                const description = getCategoryDescription(key, state);

                return (
                  <CategoryCard
                    key={key}
                    categoryName={name}
                    description={description}
                    state={state}
                    onClick={() => handleCategoryTap(key, name, state)}
                  />
                );
              })}
            </motion.div>

            {/* Retake Check-in Quiz button — deferred/hidden in v1 */}
            {/* Uncomment when multi-category retake is implemented:
          <motion.div variants={itemVariants}>
            <button
              type="button"
              className="w-full flex items-center justify-center"
              style={{
                height: '48px',
                backgroundColor: PROFILE_COLORS.textPrimary,
                borderRadius: '12px',
              }}
            >
              <span className="font-black text-xs uppercase tracking-[0.72px] text-white">
                Retake Check-in Quiz
              </span>
            </button>
          </motion.div>
          */}
          </motion.div>
        )}
      </HomeV2Layout>

      {/* Check-in transition overlay */}
      {isTransitioning && (
        <CheckInTransitionOverlay
          cloudRect={cloudRect}
          onComplete={onTransitionComplete}
        // onReplay={resetTransition}
        />
      )}

    </>
  );
}
